// Phase 2.3 — REST-backed `classService` (the Firestore `classes`
// collection, internally called `halaqas` in Postgres).
//
// Drop-in replacement for the legacy `classService` exported from
// `firebaseService.js` when REACT_APP_AUTH_BACKEND=api. The legacy API
// returns `id` + raw class fields; this V2 returns the same shape so
// `cls.name` / `cls.tenantId` / `cls.campusId` access patterns keep
// working unchanged across consumers.

import { getAccessToken } from '../contexts/AuthContextV2';

const API_BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:4001';

async function apiFetch(path, options = {}) {
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers, credentials: 'include' });
  let body = null;
  try { body = await res.json(); } catch { /* non-json */ }
  if (!res.ok) {
    const err = new Error(body?.error || `Request failed: ${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

export const classService = {
  async createClass(classData) {
    const body = {
      name: classData.name,
      description: classData.description ?? null,
      level: classData.level ?? null,
      capacity: typeof classData.capacity === 'number' ? classData.capacity : null,
      schedule: classData.schedule ?? null,
      campusId: classData.campusId ?? null,
      teacherId: classData.teacherId ?? null
    };
    const { halaqa } = await apiFetch('/api/halaqas', {
      method: 'POST',
      body: JSON.stringify(body)
    });
    return halaqa.id;
  },

  async getClasses() {
    try {
      const { halaqas } = await apiFetch('/api/halaqas');
      return halaqas;
    } catch (err) {
      if (err.status === 403) {
        // No tenant on session — match legacy behaviour (empty list).
        console.warn('classServiceV2.getClasses: no tenant context, returning empty list');
        return [];
      }
      throw err;
    }
  },

  async updateClass(classId, classData) {
    if (!classId) throw new Error('Error updating class: classId required');
    // Pass through only the fields the route accepts.
    const body = {};
    for (const k of ['name', 'description', 'level', 'capacity', 'schedule', 'campusId', 'teacherId']) {
      if (k in classData) body[k] = classData[k];
    }
    await apiFetch(`/api/halaqas/${encodeURIComponent(classId)}`, {
      method: 'PATCH',
      body: JSON.stringify(body)
    });
  },

  async deleteClass(classId) {
    if (!classId) throw new Error('Error deleting class: classId required');
    await apiFetch(`/api/halaqas/${encodeURIComponent(classId)}`, { method: 'DELETE' });
  }
};

export default classService;
